import { DOCUMENT, NgTemplateOutlet } from '@angular/common';
import { afterNextRender, ChangeDetectionStrategy, Component, computed, contentChild, DestroyRef, ElementRef, inject, input, model, NgZone, TemplateRef, viewChild, ViewEncapsulation } from '@angular/core';
import { ButtonIconComponent } from '@workbuddy/ui/v2/components/button';
import { WbTabShellComponent, WbTabDeclaration } from '../tabs/wb.tab.shell.component';
import { SURFACE_CONTEXT } from '../runtime/surface.runtime/surface.runtime.tokens';
import { surfaceBusyTooltip } from '../surface/surface.busy';
import { pointerDownCollapsesRail } from './rail.outside.pointer';

// Collapsible right-rail chrome for surface hosts. Pure chrome: tab strip,
// collapse toggle, optional pin (dock/overlay mode), collapsed vertical labels.
// Host owns tab body via projected `railTabContent`.
//
// Overlay dismiss: when pinAvailable (narrow dock) and unpinned + expanded,
// a pointerdown outside the rail collapses it. Pinned rails stay open.
// A portaled menu or dialog is not outside — see pointerDownCollapsesRail.
@Component({
  selector: 'wb-surface-rail',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  encapsulation: ViewEncapsulation.None,
  imports: [NgTemplateOutlet, ButtonIconComponent, WbTabShellComponent],
  templateUrl: './wb.surface.rail.component.html',
  styleUrls: ['./wb.surface.rail.component.less'],
  host: {
    '[class.pin-available]': '$pinAvailable()',
    '[class.is-pinned]': '$pinned()',
  },
})
export class WbSurfaceRailComponent {

  private readonly document = inject(DOCUMENT);
  private readonly destroyRef = inject(DestroyRef);
  private readonly zone = inject(NgZone);
  // Every rail mounts inside a host the surface mount created, so the page is always in reach; optional keeps bare-chrome tests working.
  private readonly page = inject(SURFACE_CONTEXT, { optional: true });

  /** Real box for hit-testing — host is display:contents so ElementRef on host is useless. */
  private readonly $railEl = viewChild<ElementRef<HTMLElement>>('railRoot');

  public $tabs = input<readonly WbTabDeclaration[]>([], { alias: 'tabs' });
  /** Tabless rail title — rendered in the icon row, not in the body. */
  public $title = input<string | undefined>(undefined, { alias: 'title' });
  public $collapsed = model<boolean>(false, { alias: 'collapsed' });
  public $activeTab = model<string | undefined>(undefined, { alias: 'activeTab' });
  /** When true (narrow/dock layout), show pin control so the overlay rail can stay open. */
  public $pinAvailable = input(false, { alias: 'pinAvailable' });
  public $pinned = model(false, { alias: 'pinned' });

  public $tabContentTemplate = contentChild<TemplateRef<{ $implicit: WbTabDeclaration }>>('railTabContent');
  /** Tabless pane (Detail). When present and `$tabs` is empty, the rail renders this instead of a tab strip. */
  public $railContentTemplate = contentChild<TemplateRef<void>>('railContent');

  /** Content is mid-work (an upload): collapse and tab switches would destroy it, so both are held. */
  protected readonly $busy = computed(() => this.page?.$busy() ?? false);

  /** Inactive tabs disable while busy; the active one stays enabled so the strip keeps a selection. */
  protected readonly $stripTabs = computed<readonly WbTabDeclaration[]>(() => {
    const tabs = this.$tabs();
    if (!this.$busy()) return tabs;
    const active = this.$activeTab() ?? tabs.find(tab => tab.visible !== false && !tab.disabled)?.value;
    return tabs.map(tab => tab.value === active ? tab : { ...tab, disabled: true });
  });

  protected readonly $toggleTooltip = computed(() => surfaceBusyTooltip(this.$busy()) ?? (this.$collapsed() ? 'Expand panel' : 'Collapse panel'));

  protected readonly $tabless = computed(() => this.$tabs().length === 0 && !!this.$railContentTemplate());

  protected readonly $collapsedTabs = computed<readonly WbTabDeclaration[]>(() => this.$tabs().filter(t => t.visible !== false));

  protected readonly $showPin = computed(() => this.$pinAvailable() && !this.$collapsed());

  protected readonly $pinIcon = computed(() => this.$pinned() ? 'fa-solid fa-thumbtack' : 'fa-regular fa-thumbtack');

  protected readonly $pinTooltip = computed(() => this.$pinned() ? 'Unpin panel' : 'Pin panel open');

  public constructor() {

    afterNextRender(() => {
      // Capture phase so we see the event before other handlers stop it.
      const onPointerDown = (event: PointerEvent) => {
        if (!this.shouldDismissOnOutsideClick()) return;
        const root = this.$railEl()?.nativeElement ?? null;
        if (!pointerDownCollapsesRail(event.target, root, this.$busy())) return;
        // Listener is outside Angular zone — re-enter so model writes refresh the host.
        this.zone.run(() => this.$collapsed.set(true));
      };
      this.document.addEventListener('pointerdown', onPointerDown, true);
      this.destroyRef.onDestroy(() => this.document.removeEventListener('pointerdown', onPointerDown, true));
    });
  }

  protected openTab(value: string): void {

    this.$activeTab.set(value);
    this.$collapsed.set(false);
  }

  protected toggleRail(): void {

    if (this.$busy()) return;
    const next = !this.$collapsed();
    this.$collapsed.set(next);
    // Collapsing clears pin — pin only applies while expanded in dock mode.
    if (next) this.$pinned.set(false);
  }

  protected togglePin(): void {

    const next = !this.$pinned();
    this.$pinned.set(next);
    if (next) this.$collapsed.set(false);
  }

  /** @angular/aria tabs the whole panel; in a populated rail, land on a real control instead. */
  protected focusPanelControl(event: FocusEvent): void {

    const panel = event.target as HTMLElement | null;
    if (panel?.getAttribute('role') !== 'tabpanel' || !panel.matches(':focus-visible')) return;
    const controls = panel.querySelectorAll<HTMLElement>('button, input, select, textarea, a[href], [tabindex]:not([tabindex="-1"])');
    const first = Array.from(controls).find(control => control !== panel && !control.hasAttribute('disabled') && control.getClientRects().length > 0 && getComputedStyle(control).visibility !== 'hidden');
    first?.focus({ preventScroll: true });
  }

  private shouldDismissOnOutsideClick(): boolean {

    // Overlay mode only: dock layout + expanded + not pinned.
    return this.$pinAvailable() && !this.$collapsed() && !this.$pinned();
  }
}
